import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CnjSearchDetailDto,
  PaginatedCnjSearchItemsDto,
  PaginatedCnjSearchMovementsDto,
  CnjSearchItemDto,
  PaginatedCnjSearchLogsDto,
  CancelCnjSearchDto,
  CnjSearchMetricsDto,
  CnjSearchMovementsAiSummaryDto,
  AiTriggerEnqueueResponseDto,
  ExternalProcessSearchResultDto,
} from '@prometeus/contracts';
import api from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import {
  useAiTriggerExecutionStore,
  selectAiTriggerRunning,
} from '../stores/ai-trigger-execution.store';
import { useEventBusStore } from '../stores/event-bus.store';
import { Breadcrumb } from '../components/Breadcrumb';
import { DistributionChart } from '../components/Metrics/DistributionChart';
import { DistributeProcessModal } from '../components/DistributeProcessModal';

const BUSCA_MOVIMENTOS_IA = 'busca_movimentos_ia';
const RESOURCE_CNJ_SEARCH_ITEM = 'cnj_search_item';
const CNJ_MOV_AI_POLL_MS = 2_000;

function mapCnjItemToExternalProcessSearchResultDto(
  item: CnjSearchItemDto,
): ExternalProcessSearchResultDto {
  return {
    numeroprocessocommascara: item.processNumber,
    link: item.link ?? '',
    nomeClasse: item.nomeClasse ?? '',
    ativo: item.isActive,
    siglaTribunal: item.siglaTribunal ?? '',
    nomeOrgao: item.nomeOrgao ?? '',
  };
}

export const Route = createFileRoute('/w/$workspaceId/processes/external-search-details')({
  validateSearch: (search: Record<string, unknown>) => ({
    searchId: typeof search.searchId === 'string' ? search.searchId : '',
  }),
  component: ExternalProcessSearchDetailsPage,
});

function formatDate(value: Date | string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR');
}

function formatDateTime(value: Date | string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
}

function statusLabel(search: CnjSearchDetailDto): string {
  if (search.status === 'queued') return 'Na fila';
  if (search.status === 'running' && search.cancelRequestedAt) return 'Cancelando...';
  if (search.status === 'running') return 'Em andamento';
  if (search.status === 'completed') return 'Concluída';
  return 'Falhou';
}

function stripHtmlToText(value: string): string {
  const raw = String(value ?? '');
  if (!raw.includes('<') || !raw.includes('>')) return raw;
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    return (doc.body?.textContent ?? '').replace(/\u00A0/g, ' ').trim();
  } catch {
    return raw.replace(/<[^>]*>/g, '').replace(/\u00A0/g, ' ').trim();
  }
}

function extractMovementsAiText(content: unknown): string {
  const raw = typeof content === 'string' ? content : JSON.stringify(content ?? '');
  const trimmed = raw.trim();

  const tryParseJson = (text: string): { summary?: unknown } | null => {
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' ? (parsed as any) : null;
    } catch {
      return null;
    }
  };

  const fenceMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  const unfenced = fenceMatch ? fenceMatch[1].trim() : trimmed;

  const parsed = tryParseJson(unfenced) ?? tryParseJson(trimmed);
  if (parsed && typeof parsed.summary === 'string') {
    return stripHtmlToText(parsed.summary);
  }

  // fallback simples para JSON parcial
  const summaryMatch = unfenced.match(/"summary"\s*:\s*"([\s\S]*?)"\s*(?:,|\})/i);
  if (summaryMatch?.[1]) {
    const unescaped = summaryMatch[1]
      .replace(/\\"/g, '"')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '\r');
    return stripHtmlToText(unescaped);
  }

  return stripHtmlToText(unfenced);
}

function escapeCsvCell(value: unknown): string {
  const text = String(value ?? '');
  const escaped = text.replace(/"/g, '""');
  return `"${escaped}"`;
}

function downloadCsv(filename: string, rows: Array<Array<unknown>>) {
  const csv = rows.map((r) => r.map(escapeCsvCell).join(';')).join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ExternalProcessSearchDetailsPage() {
  const { workspaceId } = Route.useParams();
  const { searchId } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useEventBusStore((state) => state.addToast);
  const { hasPermission } = usePermissions();
  const canViewLogs = hasPermission('members.update_role');

  const [resultsPage, setResultsPage] = useState(1);
  const [movementPage, setMovementPage] = useState(1);
  const [logsPage, setLogsPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'results' | 'logs' | 'metrics'>('results');
  const [selectedItem, setSelectedItem] = useState<CnjSearchItemDto | null>(null);
  const [distributeItem, setDistributeItem] = useState<CnjSearchItemDto | null>(null);
  const [metricsSiglaTribunal, setMetricsSiglaTribunal] = useState('');
  const [metricsNomeClasse, setMetricsNomeClasse] = useState('');
  const [movementsModalTab, setMovementsModalTab] = useState<'list' | 'ai'>('list');

  const canUseAiCredits = hasPermission('ai.use_credits');
  const [isExportingProcesses, setIsExportingProcesses] = useState(false);

  const { data: workspace } = useQuery<{ name?: string }>({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!workspaceId,
  });

  const detailQuery = useQuery<CnjSearchDetailDto, Error>({
    queryKey: ['cnj-search-detail', workspaceId, searchId],
    queryFn: async () => {
      const response = await api.get(`/processes/external-searches/${searchId}`);
      return response.data.data || response.data;
    },
    enabled: !!searchId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? 3000 : false;
    },
  });

  const cancelSearchMutation = useMutation<
    { success: true; removedQueued: number; mode: 'running' | 'queued' },
    Error,
    CancelCnjSearchDto
  >({
    mutationFn: async (payload) => {
      const response = await api.post(`/processes/external-searches/${searchId}/cancel`, payload);
      return response.data.data || response.data;
    },
    onSuccess: (data) => {
      if (data.removedQueued > 0) {
        addToast(`Cancelamento solicitado. ${data.removedQueued} pesquisa(s) da fila removidas.`, 'success');
      } else {
        addToast('Cancelamento solicitado com sucesso.', 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['cnj-searches', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['cnj-search-detail', workspaceId, searchId] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error.message || 'Erro ao cancelar pesquisa';
      addToast(Array.isArray(message) ? message.join(', ') : message, 'error');
    },
  });

  const resultsQuery = useQuery<PaginatedCnjSearchItemsDto, Error>({
    queryKey: ['cnj-search-results', workspaceId, searchId, resultsPage],
    queryFn: async () => {
      const response = await api.get(
        `/processes/external-searches/${searchId}/results?page=${resultsPage}&limit=20`,
      );
      return response.data.data || response.data;
    },
    enabled: !!searchId,
    placeholderData: (previousData) => previousData,
  });

  const movementsQuery = useQuery<PaginatedCnjSearchMovementsDto, Error>({
    queryKey: [
      'cnj-search-movements',
      workspaceId,
      searchId,
      selectedItem?.id,
      movementPage,
    ],
    queryFn: async () => {
      const response = await api.get(
        `/processes/external-searches/${searchId}/results/${selectedItem!.id}/movements?page=${movementPage}&limit=20`,
      );
      return response.data.data || response.data;
    },
    enabled: !!searchId && !!selectedItem,
    placeholderData: (previousData) => previousData,
  });

  const logsQuery = useQuery<PaginatedCnjSearchLogsDto, Error>({
    queryKey: ['cnj-search-logs', workspaceId, searchId, logsPage],
    queryFn: async () => {
      const response = await api.get(
        `/processes/external-searches/${searchId}/logs?page=${logsPage}&limit=50`,
      );
      return response.data.data || response.data;
    },
    enabled: !!searchId && canViewLogs && activeTab === 'logs',
    placeholderData: (previousData) => previousData,
  });

  const metricsQuery = useQuery<CnjSearchMetricsDto, Error>({
    queryKey: [
      'cnj-search-metrics',
      workspaceId,
      searchId,
      metricsSiglaTribunal,
      metricsNomeClasse,
    ],
    queryFn: async () => {
      const response = await api.get(`/processes/external-searches/${searchId}/metrics`, {
        params: {
          ...(metricsSiglaTribunal.trim() ? { siglaTribunal: metricsSiglaTribunal.trim() } : {}),
          ...(metricsNomeClasse.trim() ? { nomeClasse: metricsNomeClasse.trim() } : {}),
        },
      });
      return response.data.data || response.data;
    },
    enabled: !!searchId && activeTab === 'metrics',
  });

  const aiMovementsSummaryQuery = useQuery<CnjSearchMovementsAiSummaryDto | null, Error>({
    queryKey: ['cnj-search-movements-ai-summary', workspaceId, selectedItem?.id ?? ''],
    queryFn: async () => {
      const response = await api.get(
        `/processes/external-searches/${searchId}/results/${selectedItem!.id}/ai-movements-summary`,
      );
      return response.data.data ?? response.data;
    },
    enabled: !!searchId && !!selectedItem && movementsModalTab === 'ai',
  });

  const aiMovementsText = aiMovementsSummaryQuery.data?.content
    ? extractMovementsAiText(aiMovementsSummaryQuery.data.content)
    : '';

  const runningMovementsAi = useAiTriggerExecutionStore(
    selectAiTriggerRunning(
      workspaceId,
      BUSCA_MOVIMENTOS_IA,
      RESOURCE_CNJ_SEARCH_ITEM,
      selectedItem?.id ?? '',
    ),
  );
  const runningMovementsAiExecutionId = runningMovementsAi?.executionId ?? null;

  const enqueueMovementsAiMutation = useMutation<
    AiTriggerEnqueueResponseDto,
    Error,
    void
  >({
    mutationFn: async () => {
      const response = await api.post(
        `/processes/external-searches/${searchId}/results/${selectedItem!.id}/ai-movements-summary`,
      );
      return response.data.data ?? response.data;
    },
    onSuccess: (data) => {
      if (data?.executionId) {
        useAiTriggerExecutionStore.getState().setRunning(workspaceId, {
          executionId: data.executionId,
          status: 'pending',
          triggerKey: BUSCA_MOVIMENTOS_IA,
          resourceType: RESOURCE_CNJ_SEARCH_ITEM,
          resourceId: selectedItem!.id,
        });
      }
      addToast('Análise por IA enfileirada.', 'success');
    },
    onError: (err: any) => {
      if (err.response?.status === 409) {
        addToast('Já existe uma análise em andamento para este processo.', 'warning');
        return;
      }
      const message =
        err?.response?.data?.message || err.message || 'Erro ao enfileirar análise por IA';
      addToast(Array.isArray(message) ? message.join(', ') : message, 'error');
    },
  });

  useEffect(() => {
    if (!selectedItem?.id || !searchId || !workspaceId) return;
    let cancelled = false;
    const syncActive = async () => {
      try {
        const r = await api.get(
          `/processes/external-searches/${searchId}/results/${selectedItem.id}/ai-trigger-executions/active`,
        );
        const rows = r.data?.data ?? r.data;
        if (!cancelled && Array.isArray(rows)) {
          useAiTriggerExecutionStore.getState().bootstrap(workspaceId, rows, {
            resourceType: RESOURCE_CNJ_SEARCH_ITEM,
            resourceId: selectedItem.id,
          });
        }
      } catch {
        /* ignore */
      }
    };
    void syncActive();
    let timer: ReturnType<typeof setInterval> | undefined;
    if (runningMovementsAiExecutionId) {
      timer = setInterval(() => {
        void syncActive();
      }, CNJ_MOV_AI_POLL_MS);
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [selectedItem?.id, searchId, workspaceId, runningMovementsAiExecutionId]);

  if (!searchId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-[15px] p-6"
          style={{ boxShadow: '0 0 15px rgba(0, 0, 0, 0.08)' }}
        >
          <p className="text-gray-700 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Pesquisa inválida. Volte para a listagem e selecione uma pesquisa.
          </p>
        </div>
      </div>
    );
  }

  const detail = detailQuery.data;

  const handleExportProcesses = async () => {
    if (!searchId || isExportingProcesses) return;
    try {
      setIsExportingProcesses(true);
      const limit = 200;
      let page = 1;
      let totalPages = 1;
      const allItems: CnjSearchItemDto[] = [];

      while (page <= totalPages) {
        const response = await api.get(
          `/processes/external-searches/${searchId}/results?page=${page}&limit=${limit}`,
        );
        const payload = response.data?.data ?? response.data;
        const pageItems = (payload?.data ?? []) as CnjSearchItemDto[];
        allItems.push(...pageItems);
        totalPages = Number(payload?.totalPages ?? 1);
        page += 1;
      }

      const rows: Array<Array<unknown>> = [
        [
          'Numero do processo',
          'Classe',
          'Orgao',
          'Tribunal',
          'Movimentacoes',
          'Ativo',
          'Primeira visualizacao',
          'Ultima visualizacao',
          'Link',
        ],
        ...allItems.map((item) => [
          item.processNumber,
          item.nomeClasse ?? '',
          item.nomeOrgao ?? '',
          item.siglaTribunal ?? '',
          item.movementsCount,
          item.isActive ? 'Sim' : 'Nao',
          item.firstSeenAt ? formatDateTime(item.firstSeenAt) : '',
          item.lastSeenAt ? formatDateTime(item.lastSeenAt) : '',
          item.link ?? '',
        ]),
      ];

      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`busca-cnj-processos-${stamp}.csv`, rows);
      addToast(`Planilha exportada com ${allItems.length} processo(s).`, 'success');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao exportar planilha de processos';
      addToast(Array.isArray(message) ? message.join(', ') : message, 'error');
    } finally {
      setIsExportingProcesses(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <Breadcrumb
          items={[
            { label: workspace?.name ?? 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Busca CNJ', to: '/w/$workspaceId/processes/external-search', params: { workspaceId } },
            { label: 'Detalhes' },
          ]}
        />

        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-[15px]"
          style={{ boxShadow: '0 0 15px rgba(0, 0, 0, 0.08)' }}
        >
          <div
            className="flex items-start justify-between gap-3 p-6 pb-4 flex-wrap"
            style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
          >
            <div>
              <h1
                className="text-[16px] text-[#06141B] dark:text-[#F5F6F5]"
                style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}
              >
                Detalhes da Busca CNJ
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                Visualização completa e persistida da pesquisa.
              </p>
            </div>
            <div className="flex gap-2">
              {detail?.status === 'running' ? (
                <>
                  <button
                    onClick={() => cancelSearchMutation.mutate({ clearQueued: false })}
                    disabled={cancelSearchMutation.isPending || !!detail.cancelRequestedAt}
                    className="px-4 py-2 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: '#FDE68A',
                      color: '#7C2D12',
                    }}
                  >
                    {detail.cancelRequestedAt ? 'Cancelando...' : 'Cancelar'}
                  </button>
                  <button
                    onClick={() => cancelSearchMutation.mutate({ clearQueued: true })}
                    disabled={cancelSearchMutation.isPending || !!detail.cancelRequestedAt}
                    className="px-4 py-2 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: '#FEE2E2',
                      color: '#991B1B',
                    }}
                  >
                    Cancelar + limpar fila
                  </button>
                </>
              ) : null}
              <button
                type="button"
                onClick={handleExportProcesses}
                disabled={isExportingProcesses || !searchId}
                className="px-4 py-2 rounded-[10px] border border-gray-300 dark:border-gray-700 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#06141B',
                }}
              >
                {isExportingProcesses ? 'Exportando...' : 'Exportar planilha'}
              </button>
              <button
                onClick={() =>
                  navigate({
                    to: '/w/$workspaceId/processes/external-search',
                    params: { workspaceId },
                  })
                }
                className="px-4 py-2 rounded-[10px] border border-gray-300 dark:border-gray-700 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#06141B',
                }}
              >
                Voltar
              </button>
            </div>
          </div>

          <div className="p-6 pt-4">
          {detailQuery.isLoading ? (
            <div className="text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Carregando detalhes...
            </div>
          ) : detail ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Status</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }} className="text-gray-900 dark:text-gray-100">
                    {statusLabel(detail)}
                  </p>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Período</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }} className="text-gray-900 dark:text-gray-100">
                    {formatDate(detail.dateFrom)} até {formatDate(detail.dateTo)}
                  </p>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Solicitante</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }} className="text-gray-900 dark:text-gray-100">
                    {detail.requestedByName}
                  </p>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Atualizado em</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }} className="text-gray-900 dark:text-gray-100">
                    {formatDateTime(detail.updatedAt)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Número do Processo (CNJ)
                  </label>
                  <input readOnly value={detail.filters.numeroProcesso || ''} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-[10px] bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Número OAB
                  </label>
                  <input readOnly value={detail.filters.numeroOab || ''} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-[10px] bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    UF OAB
                  </label>
                  <input readOnly value={detail.filters.ufOab || ''} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-[10px] bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Nome do Advogado
                  </label>
                  <input readOnly value={detail.filters.nomeAdvogado || ''} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-[10px] bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Nome da Parte/Cliente
                  </label>
                  <input readOnly value={detail.filters.nomeParte || ''} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-[10px] bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Progresso</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }} className="text-gray-900 dark:text-gray-100">
                    {detail.progress ? `${detail.progress.monthsCompleted}/${detail.progress.monthsTotal} intervalos` : '-'}
                  </p>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Processos</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }} className="text-gray-900 dark:text-gray-100">
                    {detail.totalProcesses}
                  </p>
                </div>
                <div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Movimentações</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px' }} className="text-gray-900 dark:text-gray-100">
                    {detail.totalMovements}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-red-600 dark:text-red-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Não foi possível carregar os detalhes da pesquisa.
            </div>
          )}
          </div>
        </div>

        <div
          className="bg-white dark:bg-[#1a1a1a] rounded-[15px]"
          style={{ boxShadow: '0 0 15px rgba(0, 0, 0, 0.08)' }}
        >
          <div className="px-6 pt-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-3">
              <button
                onClick={() => setActiveTab('results')}
                className="px-3 py-2 rounded-t-[10px]"
                style={{
                  backgroundColor: activeTab === 'results' ? '#06141B' : '#F3F4F6',
                  color: activeTab === 'results' ? '#FFFFFF' : '#4B5563',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Resultados
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('metrics')}
                className="px-3 py-2 rounded-t-[10px]"
                style={{
                  backgroundColor: activeTab === 'metrics' ? '#06141B' : '#F3F4F6',
                  color: activeTab === 'metrics' ? '#FFFFFF' : '#4B5563',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Métricas
              </button>
              {canViewLogs ? (
                <button
                  onClick={() => {
                    setActiveTab('logs');
                    setLogsPage(1);
                  }}
                  className="px-3 py-2 rounded-t-[10px]"
                  style={{
                    backgroundColor: activeTab === 'logs' ? '#06141B' : '#F3F4F6',
                    color: activeTab === 'logs' ? '#FFFFFF' : '#4B5563',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  Logs (Admin)
                </button>
              ) : null}
            </div>
          </div>

          {activeTab === 'results' ? (
            <>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 text-center">
                <h2 className="text-[16px] text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
                  Resultados salvos
                </h2>
              </div>

              {resultsQuery.isLoading ? (
                <div className="p-6 text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Carregando resultados...
                </div>
              ) : (resultsQuery.data?.data || []).length === 0 ? (
                <div className="p-6 text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Nenhum processo salvo para esta pesquisa ainda.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto p-6">
                    <table className="w-full" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '34%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '10%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="text-left py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Processo
                          </th>
                          <th className="text-left py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Classe
                          </th>
                          <th className="text-left py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Órgão/Tribunal
                          </th>
                          <th className="text-center py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Movimentações
                          </th>
                          <th className="text-center py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Ação
                          </th>
                          <th className="text-center py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Distribuir
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(resultsQuery.data?.data || []).map((item) => (
                          <tr key={item.id} className="border-b border-gray-200 dark:border-gray-700">
                            <td className="py-3 px-2 align-top" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              <div className="text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis" title={item.processNumber}>
                                {item.processNumber}
                              </div>
                              {item.link ? (
                                <a href={item.link} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 text-[11px] underline">
                                  PJE
                                </a>
                              ) : null}
                            </td>
                            <td className="py-3 px-2 text-gray-900 dark:text-gray-100 align-top" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              {item.nomeClasse || '-'}
                            </td>
                            <td className="py-3 px-2 align-top" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              <div className="text-gray-900 dark:text-gray-100">{item.nomeOrgao || '-'}</div>
                              <div className="text-gray-500 dark:text-gray-400">{item.siglaTribunal || '-'}</div>
                            </td>
                            <td className="py-3 px-2 text-gray-900 dark:text-gray-100 text-center align-middle" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              {item.movementsCount}
                            </td>
                            <td className="py-3 px-2 text-center align-middle">
                              <button
                                onClick={() => {
                                  setSelectedItem(item);
                                  setMovementPage(1);
                                  setMovementsModalTab('list');
                                }}
                                className="px-3 py-1.5 rounded-[10px] text-white"
                                style={{
                                  backgroundColor: '#06141B',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                }}
                              >
                                Ver movimentações
                              </button>
                            </td>
                            <td className="py-3 px-2 text-center align-middle">
                              <button
                                onClick={() => {
                                  setDistributeItem(item);
                                }}
                                className="px-3 py-1.5 rounded-[10px] text-white"
                                style={{
                                  backgroundColor: '#06141B',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                }}
                              >
                                Distribuir
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(resultsQuery.data?.totalPages || 1) > 1 && (
                    <div className="flex justify-center items-center gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setResultsPage((p) => Math.max(1, p - 1))}
                        disabled={resultsPage === 1}
                        className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                      >
                        &lt;
                      </button>
                      <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2D3748' }}>
                        Página {resultsPage} de {resultsQuery.data?.totalPages || 1}
                      </span>
                      <button
                        onClick={() => setResultsPage((p) => Math.min(resultsQuery.data?.totalPages || 1, p + 1))}
                        disabled={resultsPage === (resultsQuery.data?.totalPages || 1)}
                        className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                      >
                        &gt;
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : activeTab === 'metrics' ? (
            <>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-[16px] text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
                  Métricas
                </h2>
                <p className="mt-1 text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                  Quantidade de processos por ano (extraído do CNJ), classe, tribunal e órgão (vara). Use os filtros para refinar os gráficos.
                </p>
              </div>

              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-4 items-end">
                <div className="min-w-[180px]">
                  <label
                    className="block text-xs text-gray-500 dark:text-gray-400 mb-1"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    Tribunal
                  </label>
                  <select
                    value={metricsSiglaTribunal}
                    onChange={(e) => setMetricsSiglaTribunal(e.target.value)}
                    className="w-full px-3 py-2 rounded-[10px] border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  >
                    <option value="">Todos</option>
                    {(metricsQuery.data?.availableFilters.siglasTribunal ?? []).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[220px] flex-1 max-w-md">
                  <label
                    className="block text-xs text-gray-500 dark:text-gray-400 mb-1"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    Classe
                  </label>
                  <select
                    value={metricsNomeClasse}
                    onChange={(e) => setMetricsNomeClasse(e.target.value)}
                    className="w-full px-3 py-2 rounded-[10px] border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                  >
                    <option value="">Todas</option>
                    {(metricsQuery.data?.availableFilters.nomesClasse ?? []).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {metricsQuery.isLoading ? (
                <div className="p-6 text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Carregando métricas...
                </div>
              ) : metricsQuery.isError ? (
                <div className="p-6 text-red-600 dark:text-red-400" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Não foi possível carregar as métricas.
                </div>
              ) : metricsQuery.data ? (
                <div className="p-6 space-y-8">
                  <p className="text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                    Total de processos (após filtros):{' '}
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{metricsQuery.data.totalProcesses}</span>
                  </p>
                  <DistributionChart
                    title="Processos por ano (CNJ)"
                    variant="bar"
                    maxItems={40}
                    data={metricsQuery.data.byYear.map((b) => ({ label: b.key, count: b.count }))}
                  />
                  <DistributionChart
                    title="Processos por classe"
                    variant="bar"
                    maxItems={15}
                    data={metricsQuery.data.byClasse.map((b) => ({ label: b.key, count: b.count }))}
                  />
                  <DistributionChart
                    title="Processos por tribunal"
                    variant="bar"
                    maxItems={15}
                    data={metricsQuery.data.byTribunal.map((b) => ({ label: b.key, count: b.count }))}
                  />
                  <DistributionChart
                    title="Processos por vara (órgão)"
                    variant="bar"
                    maxItems={15}
                    data={metricsQuery.data.byVara.map((b) => ({ label: b.key, count: b.count }))}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-[16px] text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>
                  Log da Pesquisa (Admin)
                </h2>
                <p className="mt-1 text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                  Histórico passo a passo de chamadas, persistência e erros para diagnóstico.
                </p>
              </div>

              {!canViewLogs ? (
                <div className="p-6 text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Você não tem permissão para visualizar logs desta pesquisa.
                </div>
              ) : logsQuery.isLoading ? (
                <div className="p-6 text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Carregando logs...
                </div>
              ) : (logsQuery.data?.data || []).length === 0 ? (
                <div className="p-6 text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Nenhum log registrado para esta pesquisa.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto p-6">
                    <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th className="text-left py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Data/Hora
                          </th>
                          <th className="text-left py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Nível
                          </th>
                          <th className="text-left py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Etapa
                          </th>
                          <th className="text-left py-3 px-2 text-xs uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                            Mensagem
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(logsQuery.data?.data || []).map((log) => (
                          <tr key={log.id} className="border-b border-gray-200 dark:border-gray-700 align-top">
                            <td className="py-3 px-2 text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              {formatDateTime(log.createdAt)}
                            </td>
                            <td className="py-3 px-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              <span
                                className="px-2 py-1 rounded-[8px]"
                                style={{
                                  backgroundColor: log.level === 'error' ? '#FEE2E2' : log.level === 'warn' ? '#FEF3C7' : '#E5E7EB',
                                  color: log.level === 'error' ? '#991B1B' : log.level === 'warn' ? '#92400E' : '#374151',
                                }}
                              >
                                {log.level.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              {log.stage}
                            </td>
                            <td className="py-3 px-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                              <div className="text-gray-900 dark:text-gray-100">{log.message}</div>
                              {log.metadata ? (
                                <pre className="mt-2 whitespace-pre-wrap break-words text-[11px] text-gray-500 dark:text-gray-400">
                                  {JSON.stringify(log.metadata, null, 2)}
                                </pre>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(logsQuery.data?.totalPages || 1) > 1 && (
                    <div className="flex justify-center items-center gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                        disabled={logsPage === 1}
                        className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                      >
                        &lt;
                      </button>
                      <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2D3748' }}>
                        Página {logsPage} de {logsQuery.data?.totalPages || 1}
                      </span>
                      <button
                        onClick={() => setLogsPage((p) => Math.min(logsQuery.data?.totalPages || 1, p + 1))}
                        disabled={logsPage === (logsQuery.data?.totalPages || 1)}
                        className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                      >
                        &gt;
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {selectedItem
          ? createPortal(
              <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-2 sm:p-4"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedItem(null);
                    setMovementPage(1);
                    setMovementsModalTab('list');
                  }
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cnj-movements-modal-title"
              >
                <div
                  className="bg-white dark:bg-[#1a1a1a] w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh]"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    borderRadius: 15,
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="flex items-center justify-between flex-shrink-0 p-5 pb-4 gap-3"
                    style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
                  >
                    <div className="min-w-0">
                      <h2
                        id="cnj-movements-modal-title"
                        className="text-[16px] text-[#06141B] dark:text-[#F5F6F5] truncate"
                        style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}
                      >
                        Movimentações
                      </h2>
                      <p className="text-gray-500 dark:text-gray-400 mt-1 truncate text-[12px]" title={selectedItem.processNumber}>
                        {selectedItem.processNumber}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedItem(null);
                        setMovementPage(1);
                        setMovementsModalTab('list');
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-80 flex-shrink-0 bg-[#06141B] dark:bg-[#F5F6F5] text-[#F5F6F5] dark:text-[#06141B]"
                      aria-label="Fechar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div
                    className="flex flex-wrap items-center justify-between gap-2 px-5 pb-3 flex-shrink-0"
                    style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
                  >
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setMovementsModalTab('list')}
                        className="px-3 py-1.5 rounded-[10px] text-[12px] transition-opacity"
                        style={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: movementsModalTab === 'list' ? 600 : 400,
                          backgroundColor: movementsModalTab === 'list' ? '#06141B' : '#F3F4F6',
                          color: movementsModalTab === 'list' ? '#F9FAFB' : '#374151',
                        }}
                      >
                        Lista
                      </button>
                      <button
                        type="button"
                        onClick={() => setMovementsModalTab('ai')}
                        className="px-3 py-1.5 rounded-[10px] text-[12px] transition-opacity dark:text-gray-100"
                        style={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: movementsModalTab === 'ai' ? 600 : 400,
                          backgroundColor:
                            movementsModalTab === 'ai' ? '#06141B' : '#F3F4F6',
                          color: movementsModalTab === 'ai' ? '#F9FAFB' : '#374151',
                        }}
                      >
                        Resumo IA
                      </button>
                    </div>
                    {canUseAiCredits ? (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          disabled={
                            enqueueMovementsAiMutation.isPending || !!runningMovementsAi
                          }
                          onClick={() => enqueueMovementsAiMutation.mutate()}
                          className="px-3 py-1.5 rounded-[10px] text-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            backgroundColor: '#0F766E',
                            color: '#F9FAFB',
                          }}
                        >
                          {runningMovementsAi
                            ? 'Processando…'
                            : movementsModalTab === 'ai'
                              ? 'Gerar novamente'
                              : 'Analisar com IA'}
                        </button>
                        <span
                          className="text-[10px] text-gray-500 dark:text-gray-400 max-w-[220px] text-right"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          Acima de 5 movimentações o custo em créditos é o dobro.
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div className="p-5 pt-4 min-h-0 flex-1 overflow-y-auto">
                    {movementsModalTab === 'list' ? (
                      <>
                        {movementsQuery.isLoading ? (
                          <div
                            className="text-gray-600 dark:text-gray-300"
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                          >
                            Carregando movimentações...
                          </div>
                        ) : (movementsQuery.data?.data || []).length === 0 ? (
                          <div
                            className="text-gray-600 dark:text-gray-300"
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                          >
                            Nenhuma movimentação encontrada para este processo.
                          </div>
                        ) : (
                          <div className="overflow-x-auto -mx-1">
                            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th
                                    className="text-left py-3 px-2 text-xs uppercase tracking-wider"
                                    style={{
                                      fontFamily: 'Poppins, sans-serif',
                                      color: '#9CA3AF',
                                      fontWeight: 400,
                                    }}
                                  >
                                    Data
                                  </th>
                                  <th
                                    className="text-left py-3 px-2 text-xs uppercase tracking-wider"
                                    style={{
                                      fontFamily: 'Poppins, sans-serif',
                                      color: '#9CA3AF',
                                      fontWeight: 400,
                                    }}
                                  >
                                    Tipo
                                  </th>
                                  <th
                                    className="text-left py-3 px-2 text-xs uppercase tracking-wider"
                                    style={{
                                      fontFamily: 'Poppins, sans-serif',
                                      color: '#9CA3AF',
                                      fontWeight: 400,
                                    }}
                                  >
                                    Descrição
                                  </th>
                                  <th
                                    className="text-left py-3 px-2 text-xs uppercase tracking-wider"
                                    style={{
                                      fontFamily: 'Poppins, sans-serif',
                                      color: '#9CA3AF',
                                      fontWeight: 400,
                                    }}
                                  >
                                    Link
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(movementsQuery.data?.data || []).map((movement) => (
                                  <tr
                                    key={movement.id}
                                    className="border-b border-gray-200 dark:border-gray-700"
                                  >
                                    <td
                                      className="py-3 px-2 text-gray-900 dark:text-gray-100"
                                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                                    >
                                      {formatDateTime(movement.publishedAt)}
                                    </td>
                                    <td
                                      className="py-3 px-2 text-gray-900 dark:text-gray-100"
                                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                                    >
                                      {movement.movementType || '-'}
                                    </td>
                                    <td
                                      className="py-3 px-2 text-gray-900 dark:text-gray-100"
                                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                                    >
                                      {movement.description || '-'}
                                    </td>
                                    <td
                                      className="py-3 px-2"
                                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                                    >
                                      {movement.link ? (
                                        <a
                                          href={movement.link}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-600 dark:text-blue-400 underline"
                                        >
                                          Abrir
                                        </a>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-3">
                        {aiMovementsSummaryQuery.isLoading ? (
                          <p
                            className="text-gray-600 dark:text-gray-300"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                          >
                            Carregando resumo…
                          </p>
                        ) : runningMovementsAi ? (
                          <p
                            className="text-gray-600 dark:text-gray-300"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                          >
                            Gerando resumo com IA. Você pode fechar este modal; o resultado ficará salvo nesta
                            aba.
                          </p>
                        ) : null}
                        {aiMovementsText ? (
                          <div
                            className="whitespace-pre-wrap text-gray-900 dark:text-gray-100 text-[13px] leading-relaxed"
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                          >
                            {aiMovementsText}
                          </div>
                        ) : null}
                        {!runningMovementsAi &&
                        !aiMovementsSummaryQuery.isLoading &&
                        !aiMovementsSummaryQuery.data?.content ? (
                          <p
                            className="text-gray-500 dark:text-gray-400"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
                          >
                            {canUseAiCredits
                              ? 'Ainda não há resumo. Use “Analisar com IA” para gerar.'
                              : 'Resumo de IA indisponível para seu perfil de permissões.'}
                          </p>
                        ) : null}
                        {aiMovementsSummaryQuery.data?.updatedAt ? (
                          <p
                            className="text-[11px] text-gray-400 dark:text-gray-500"
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                          >
                            Atualizado em {formatDateTime(aiMovementsSummaryQuery.data.updatedAt)}
                            {typeof aiMovementsSummaryQuery.data.movementCount === 'number'
                              ? ` · ${aiMovementsSummaryQuery.data.movementCount} movimentações`
                              : ''}
                            {typeof aiMovementsSummaryQuery.data.creditsUsed === 'number'
                              ? ` · ${aiMovementsSummaryQuery.data.creditsUsed} crédito(s)`
                              : ''}
                          </p>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {movementsModalTab === 'list' &&
                  (movementsQuery.data?.totalPages || 1) > 1 ? (
                    <div
                      className="px-5 py-3 flex justify-center items-center gap-2 flex-shrink-0"
                      style={{ borderTop: '1px solid rgba(6, 20, 27, 0.08)' }}
                    >
                      <button
                        type="button"
                        onClick={() => setMovementPage((p) => Math.max(1, p - 1))}
                        disabled={movementPage === 1}
                        className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                      >
                        &lt;
                      </button>
                      <span className="text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                        Página {movementPage} de {movementsQuery.data?.totalPages || 1}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setMovementPage((p) => Math.min(movementsQuery.data?.totalPages || 1, p + 1))
                        }
                        disabled={movementPage === (movementsQuery.data?.totalPages || 1)}
                        className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', backgroundColor: '#F9FAFB' }}
                      >
                        &gt;
                      </button>
                    </div>
                  ) : null}

                  <div
                    className="px-5 py-4 flex justify-end gap-3 flex-shrink-0"
                    style={{ borderTop: '1px solid rgba(6, 20, 27, 0.08)' }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedItem(null);
                        setMovementPage(1);
                        setMovementsModalTab('list');
                      }}
                      className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 12,
                        fontWeight: 500,
                        backgroundColor: '#06141B',
                        color: '#F9FAFB',
                      }}
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              </div>,
              document.body,
            )
          : null}

        {distributeItem ? (
          <DistributeProcessModal
            workspaceId={workspaceId}
            processData={mapCnjItemToExternalProcessSearchResultDto(distributeItem)}
            onClose={() => setDistributeItem(null)}
            onSuccess={() => {
              setDistributeItem(null);
              queryClient.invalidateQueries({
                queryKey: ['cnj-search-results', workspaceId, searchId],
              });
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
